import { CommonModule, formatDate } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { GoogleMapsModule } from '@angular/google-maps';
import { MenuItem } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ChartModule } from 'primeng/chart';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ToolbarModule } from 'primeng/toolbar';
import { Observable, Subscription, catchError, finalize, forkJoin, interval, map, of, switchMap } from 'rxjs';
import { APIListWrapper } from '../models/common/api-list-wrapper.model';
import { APIResponseWrapper } from '../models/common/api-response-wrapper';
import { ReportsService } from '../services/reports.service';
import { SessionStorageService } from '../services/session-storage.service';
import { PageDataPanelComponent } from '../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../shared/page-layout/page-hero.component';
import { PageMetric } from '../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../shared/page-layout/page-shell.component';
import { TransportationAgency } from '../company/transportation-agency/transportation-agency.model';
import { FreightForwarder } from '../company/freight-forwarder/freight-forwarder.model';
import { Driver } from './driver/driver.model';
import { DriverService } from './driver/driver.service';
import { MaintenanceAlert, MaintenanceAnalyticsDashboard } from './maintenance/models/maintenance-analytics.model';
import { MaintenanceDashboard, MaintenanceVehicleAvailability } from './maintenance/models/maintenance-availability.model';
import { MaintenanceWorkOrder } from './maintenance/models/maintenance-work-order.model';
import { MaintenanceService } from './maintenance/maintenance.service';
import { FleetAvailabilityResponse, FleetAvailabilityRow, Mobilization, MobilizationStatus } from './mobilization/models/mobilization.model';
import { MobilizationService } from './mobilization/mobilization.service';
import { GpsLocation, GpsLocationHistoryPoint, GpsSelectOption, GpsTrackingSource, GpsVehicleEvent, GpsVehicleLive } from './gps-tracking/gps-tracking.model';
import { GpsTrackingService } from './gps-tracking/gps-tracking.service';
import { GpsMarkerAnimator } from './gps-tracking/gps-marker-animator';
import { gpsTruckMarkerIcon } from './gps-tracking/gps-truck-marker';
import { VehicleMin } from './vehicle/vehicle-min.model';
import { VehicleService } from './vehicle/vehicle.service';
import { QuickFleetOnboardingDialogComponent } from './quick-fleet-onboarding/quick-fleet-onboarding-dialog.component';
import { CrmService } from '../crm/crm.service';

type FleetAssetSource = 'Owned' | 'Freelance' | 'Rental' | 'Transporter';
type FleetTone = 'success' | 'info' | 'warn' | 'danger' | 'secondary';

interface FleetVehicleRow extends VehicleMin {
    source: FleetAssetSource;
    gpsStatus: 'Online' | 'Offline';
    maintenanceStatus?: string;
    availabilityReason?: string;
}

interface FleetDriverRow extends Driver {
    source: FleetAssetSource;
}

interface FleetScorecard {
    label: string;
    value: string;
    detail: string;
    icon: string;
    tone: FleetTone;
    progress: number;
}

interface ReadinessItem {
    label: string;
    value: string | number;
    detail: string;
    severity: FleetTone;
}

interface MaintenanceQueueItem {
    type: string;
    title: string;
    asset: string;
    owner: string;
    status: string;
    severity: FleetTone;
    actionUrl?: string;
    workOrderId?: number;
}

@Component({
    selector: 'app-fleet',
    templateUrl: './fleet.component.html',
    styleUrls: ['./fleet.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        ToastModule,
        ButtonModule,
        TableModule,
        InputTextModule,
        DatePickerModule,
        IftaLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
        SelectModule,
        ToolbarModule,
        BreadcrumbModule,
        CardModule,
        GoogleMapsModule,
        TagModule,
        ChartModule,
        ProgressSpinnerModule,
        BadgeModule,
        ProgressBarModule,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageDataPanelComponent,
        QuickFleetOnboardingDialogComponent
    ],
    providers: [
        ReportsService,
        SessionStorageService,
        VehicleService,
        DriverService,
        MobilizationService,
        MaintenanceService,
        GpsTrackingService
    ]
})
export class FleetComponent implements OnInit, OnDestroy {
    isLoading = false;
    isTelematicsLoading = false;
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard' };
    companyId = 0;
    transportationAgencyId = 0;
    quickFleetOnboardingVisible = false;

    vehicleList: FleetVehicleRow[] = [];
    driverList: FleetDriverRow[] = [];
    vehicleTableRows: FleetVehicleRow[] = [];
    driverTableRows: FleetDriverRow[] = [];
    vehicleTableTotalRecords = 0;
    driverTableTotalRecords = 0;
    vehicleTableFirst = 0;
    driverTableFirst = 0;
    vehicleTableRowsPerPage = 10;
    driverTableRowsPerPage = 10;
    vehicleTableSearch = '';
    driverTableSearch = '';
    vehicleSourceFilter: FleetAssetSource | null = null;
    driverSourceFilter: FleetAssetSource | null = null;
    vehicleStateFilter: string | null = null;
    driverDocumentFilter: string | null = null;
    mobilizations: Mobilization[] = [];
    maintenanceAvailability: MaintenanceVehicleAvailability[] = [];
    maintenanceAlerts: MaintenanceAlert[] = [];
    workOrders: MaintenanceWorkOrder[] = [];
    maintenanceDashboard?: MaintenanceDashboard;
    maintenanceAnalytics?: MaintenanceAnalyticsDashboard;
    availability?: FleetAvailabilityResponse;
    gpsLiveVehicles: GpsVehicleLive[] = [];
    gpsSources: GpsTrackingSource[] = [];
    selectedGpsSourceKey = '';
    selectedTrackingVehicle?: FleetVehicleRow;
    selectedTrackingLive?: GpsVehicleLive;
    gpsEvents: GpsVehicleEvent[] = [];
    gpsHistory: GpsLocationHistoryPoint[] = [];
    gpsHistoryRange: Date[] = [new Date(Date.now() - 24 * 60 * 60 * 1000), new Date()];
    gpsPollingEnabled = true;
    replayIndex = 0;
    replayPlaying = false;
    replaySpeed = 1;
    currentReplayPoint?: GpsLocationHistoryPoint;

    currentTransportationAgency: TransportationAgency | null;
    currentFreightForwarder: FreightForwarder | null;
    sourceFilterOptions: { label: string; value: FleetAssetSource }[] = [
        { label: 'Owned', value: 'Owned' },
        { label: 'Freelance', value: 'Freelance' },
        { label: 'Rental', value: 'Rental' },
        { label: 'Transporter', value: 'Transporter' }
    ];
    vehicleStateFilterOptions = [
        { label: 'Moving', value: 'Moving' },
        { label: 'Idle', value: 'Idle' },
        { label: 'Offline', value: 'Offline' },
        { label: 'Valid Docs', value: 'Valid' },
        { label: 'Due Soon', value: 'Due Soon' },
        { label: 'Expired', value: 'Expired' },
        { label: 'Missing Docs', value: 'Missing' }
    ];
    documentFilterOptions = [
        { label: 'Valid', value: 'Valid' },
        { label: 'Due Soon', value: 'Due Soon' },
        { label: 'Expired', value: 'Expired' },
        { label: 'Missing', value: 'Missing' }
    ];

    zoom = 12;
    center: google.maps.LatLngLiteral = { lat: 21.485811, lng: 39.192505 };
    options: google.maps.MapOptions = {
        mapTypeId: 'roadmap',
        zoomControl: true,
        scrollwheel: true,
        disableDoubleClickZoom: true,
        maxZoom: 15,
        minZoom: 8
    };

    readonly replaySpeeds: GpsSelectOption<number>[] = [
        { label: '1x', value: 1 },
        { label: '2x', value: 2 },
        { label: '10x', value: 10 },
        { label: '100x', value: 100 }
    ];

    readonly derivedAlertEventTypes = new Set([
        'GPS_SIGNAL_LOST',
        'GPS_SIGNAL_RESTORED',
        'GPS_SIGNAL_ACQUIRED',
        'IGNITION_ON',
        'IGNITION_OFF',
        'MOVING',
        'STOPPED'
    ]);

    assetStatusChartData: any;
    ownershipChartData: any;
    mobilizationStatusChartData: any;
    mobilizationTrendChartData: any;
    maintenanceCostChartData: any;
    maintenanceDowntimeChartData: any;
    driverReadinessRadarData: any;
    riskPolarChartData: any;
    speedDistanceBubbleData: any;

    chartOptions: any = {
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: { boxWidth: 10, usePointStyle: true }
            }
        }
    };

    axisChartOptions: any = {
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: { boxWidth: 10, usePointStyle: true }
            }
        },
        scales: {
            x: { grid: { color: '#eef2f7' }, ticks: { color: '#64748b' } },
            y: { beginAtZero: true, grid: { color: '#eef2f7' }, ticks: { color: '#64748b' } }
        }
    };

    horizontalChartOptions: any = {
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
            legend: { display: false }
        },
        scales: {
            x: { beginAtZero: true, grid: { color: '#eef2f7' }, ticks: { color: '#64748b' } },
            y: { grid: { display: false }, ticks: { color: '#64748b' } }
        }
    };

    radarChartOptions: any = {
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false }
        },
        scales: {
            r: {
                min: 0,
                max: 100,
                ticks: { stepSize: 25, backdropColor: 'transparent', color: '#64748b' },
                grid: { color: '#e2e8f0' },
                angleLines: { color: '#e2e8f0' },
                pointLabels: { color: '#334155', font: { weight: 700 } }
            }
        }
    };

    bubbleChartOptions: any = {
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: { boxWidth: 10, usePointStyle: true }
            },
            tooltip: {
                callbacks: {
                    label: (context: any) => {
                        const raw = context.raw || {};
                        return `${context.dataset.label}: ${raw.plate || '-'} | ${raw.x || 0} km | ${raw.y || 0} km/h`;
                    }
                }
            }
        },
        scales: {
            x: {
                beginAtZero: true,
                title: { display: true, text: 'Distance today (km)' },
                grid: { color: '#eef2f7' },
                ticks: { color: '#64748b' }
            },
            y: {
                beginAtZero: true,
                title: { display: true, text: 'Speed (km/h)' },
                grid: { color: '#eef2f7' },
                ticks: { color: '#64748b' }
            }
        }
    };

    private readonly pageSize = 500;
    private gpsPollSub?: Subscription;
    private replayTimer?: ReturnType<typeof setTimeout>;
    private followReplayMarker = false;
    private readonly vehicleMarkerAnimators = new Map<string, GpsMarkerAnimator>();
    private readonly replayMarkerAnimator = new GpsMarkerAnimator(position => {
        if (this.followReplayMarker) {
            this.center = position;
        }
    });
    private readonly moneyFormatter = new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0
    });

    constructor(
        protected sessionStorageService: SessionStorageService,
        private router: Router,
        private vehicleService: VehicleService,
        private driverService: DriverService,
        private crmService: CrmService,
        private mobilizationService: MobilizationService,
        private maintenanceService: MaintenanceService,
        private gpsTrackingService: GpsTrackingService
    ) {
        this.currentTransportationAgency = this.sessionStorageService.getTransportationAgency();
        this.currentFreightForwarder = this.sessionStorageService.getFreightForwarder();
    }

    ngOnInit(): void {
        const company = this.sessionStorageService.getCompany();
        this.companyId = company?.id ?? 0;
        this.transportationAgencyId = company?.transportationAgencyId ?? 0;
        this.breadcrumbItems = [{ label: 'Fleet', routerLink: '/fleet' }];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/dashboard' };
        this.buildCharts();
        this.loadDashboard();
        this.setCurrentLocation();
    }

    ngOnDestroy(): void {
        this.gpsPollSub?.unsubscribe();
        this.pauseReplay();
        this.vehicleMarkerAnimators.forEach(animator => animator.destroy());
        this.replayMarkerAnimator.destroy();
    }

    loadDashboard(): void {
        if (!this.companyId) {
            return;
        }

        this.isLoading = true;
        const periodFrom = new Date();
        periodFrom.setDate(periodFrom.getDate() - 30);
        const periodTo = new Date();
        const availabilityFrom = new Date();
        const availabilityTo = new Date();
        availabilityTo.setDate(availabilityTo.getDate() + 7);

        forkJoin({
            ownedVehicles: this.safeList(this.transportationAgencyId
                ? this.vehicleService.findVehicleMinsByTransportationAgencyId(this.transportationAgencyId)
                : null),
            freelanceVehicles: this.safeList(this.vehicleService.getFreelanceVehicleMinsByCompanyId(this.companyId)),
            rentalVehicles: this.safeList(this.vehicleService.getRentalVehicleMinsByCompanyId(this.companyId)),
            transporterFleet: this.transporterFleetResources(),
            ownedDrivers: this.safeList(this.transportationAgencyId
                ? this.driverService.findDriversByTransportationAgencyId(0, this.pageSize, this.transportationAgencyId)
                : null),
            freelanceDrivers: this.safeList(this.driverService.getFreelanceDriversByCompanyId(0, this.pageSize, this.companyId)),
            rentalDrivers: this.safeList(this.driverService.getRentalDriversByCompanyId(0, this.pageSize, this.companyId)),
            mobilizations: this.safeList(this.mobilizationService.searchMobilizations({ owningCompanyId: this.companyId, page: 0, pageSize: this.pageSize })),
            maintenanceDashboard: this.safeResponse(this.maintenanceService.getDashboard(this.companyId)),
            maintenanceAnalytics: this.safeResponse(this.maintenanceService.getAnalytics(this.companyId, periodFrom, periodTo)),
            maintenanceAlerts: this.safeList(this.maintenanceService.getAlerts(this.companyId, periodFrom, periodTo)),
            maintenanceAvailability: this.safeList(this.maintenanceService.searchAvailability({ owningCompanyId: this.companyId, page: 0, pageSize: this.pageSize })),
            workOrders: this.safeList(this.maintenanceService.searchWorkOrders({ owningCompanyId: this.companyId, page: 0, pageSize: this.pageSize })),
            availability: this.safeResponse(this.mobilizationService.getAvailability({
                owningCompanyId: this.companyId,
                dateFrom: availabilityFrom,
                dateTo: availabilityTo
            }))
        }).pipe(finalize(() => this.isLoading = false)).subscribe(data => {
            this.vehicleList = [
                ...this.toFleetVehicles(data.ownedVehicles.items, 'Owned'),
                ...this.toFleetVehicles(data.freelanceVehicles.items, 'Freelance'),
                ...this.toFleetVehicles(data.rentalVehicles.items, 'Rental'),
                ...this.toFleetVehicles(data.transporterFleet.vehicles, 'Transporter')
            ];
            this.driverList = [
                ...this.toFleetDrivers(data.ownedDrivers.items, 'Owned'),
                ...this.toFleetDrivers(data.freelanceDrivers.items, 'Freelance'),
                ...this.toFleetDrivers(data.rentalDrivers.items, 'Rental'),
                ...this.toFleetDrivers(data.transporterFleet.drivers, 'Transporter')
            ];
            this.mobilizations = data.mobilizations.items ?? [];
            this.maintenanceDashboard = data.maintenanceDashboard;
            this.maintenanceAnalytics = data.maintenanceAnalytics;
            this.maintenanceAlerts = data.maintenanceAlerts.items ?? [];
            this.maintenanceAvailability = data.maintenanceAvailability.items ?? [];
            this.workOrders = data.workOrders.items ?? [];
            this.availability = data.availability;
            this.applyMaintenanceAvailability();
            this.buildCharts();
            this.loadVehicleTable({ first: this.vehicleTableFirst, rows: this.vehicleTableRowsPerPage });
            this.loadDriverTable({ first: this.driverTableFirst, rows: this.driverTableRowsPerPage });
            this.loadTelematics();
        });
    }

    fleetMetrics(): PageMetric[] {
        const totalVehicles = this.vehicleList.length;
        const activeMobilizationVehicles = this.activeMobilizationVehicleCount();
        const utilization = this.percentValue(activeMobilizationVehicles, totalVehicles);
        const driverCoverage = this.percentValue(this.assignedVehicleCount(), totalVehicles);
        const pmCompliance = this.pmCompliancePercent();
        const costPerKm = this.maintenanceAnalytics?.costPerKilometer;

        return [
            {
                label: 'Fleet Readiness',
                value: `${this.dispatchableVehicleCount()} / ${totalVehicles}`,
                hint: 'Dispatchable vehicles',
                tone: this.dispatchableVehicleCount() < totalVehicles ? 'warn' : 'success'
            },
            {
                label: 'Asset Utilization',
                value: this.percentLabel(utilization),
                hint: `${activeMobilizationVehicles} vehicles mobilized`,
                tone: utilization >= 75 ? 'success' : utilization >= 45 ? 'info' : 'warn'
            },
            {
                label: 'Driver Coverage',
                value: this.percentLabel(driverCoverage),
                hint: `${this.assignedVehicleCount()} vehicles with drivers`,
                tone: driverCoverage >= 90 ? 'success' : driverCoverage >= 65 ? 'warn' : 'danger'
            },
            {
                label: 'Maintenance Risk',
                value: this.maintenanceRiskCount(),
                hint: 'Alerts, defects, PM, availability',
                tone: this.maintenanceRiskCount() > 0 ? 'danger' : 'success'
            },
            {
                label: 'PM Compliance',
                value: this.percentLabel(pmCompliance),
                hint: 'Preventive maintenance adherence',
                tone: pmCompliance >= 90 ? 'success' : pmCompliance >= 75 ? 'warn' : 'danger'
            },
            {
                label: 'Telematics Coverage',
                value: `${this.onlineVehicleCount()} / ${totalVehicles}`,
                hint: 'Vehicles reporting GPS',
                tone: this.onlineVehicleCount() === totalVehicles && totalVehicles > 0 ? 'success' : 'info'
            },
            {
                label: 'Distance Today',
                value: `${this.totalDistanceCovered()} km`,
                hint: 'GPS reported distance',
                tone: 'info'
            },
            {
                label: 'Cost / KM',
                value: costPerKm === undefined || costPerKm === null ? '-' : `${costPerKm.toFixed(2)} SAR`,
                hint: 'Maintenance normalized cost',
                tone: (costPerKm ?? 0) > 0 ? 'warn' : 'default'
            }
        ];
    }

    scorecards(): FleetScorecard[] {
        const totalVehicles = this.vehicleList.length;
        const totalDrivers = this.driverList.length;
        const utilization = this.percentValue(this.activeMobilizationVehicleCount(), totalVehicles);
        const availability = this.percentValue(this.dispatchableVehicleCount(), totalVehicles);
        const driverCompliance = this.driverCompliancePercent();
        const pmCompliance = this.pmCompliancePercent();
        const rentalShare = this.percentValue(this.countVehiclesBySource('Rental'), totalVehicles);
        const telematics = this.percentValue(this.onlineVehicleCount(), totalVehicles);

        return [
            {
                label: 'Asset Availability',
                value: this.percentLabel(availability),
                detail: `${this.dispatchableVehicleCount()} dispatchable of ${totalVehicles}`,
                icon: 'pi pi-check-circle',
                tone: availability >= 90 ? 'success' : availability >= 70 ? 'warn' : 'danger',
                progress: availability
            },
            {
                label: 'Mobilization Load',
                value: this.percentLabel(utilization),
                detail: `${this.activeMobilizations().length} active assignments`,
                icon: 'pi pi-briefcase',
                tone: utilization >= 75 ? 'success' : utilization >= 45 ? 'info' : 'warn',
                progress: utilization
            },
            {
                label: 'Driver Compliance',
                value: this.percentLabel(driverCompliance),
                detail: `${this.validDriverDocumentCount()} compliant of ${totalDrivers}`,
                icon: 'pi pi-id-card',
                tone: driverCompliance >= 95 ? 'success' : driverCompliance >= 80 ? 'warn' : 'danger',
                progress: driverCompliance
            },
            {
                label: 'PM Discipline',
                value: this.percentLabel(pmCompliance),
                detail: `${this.overduePmCount()} overdue PM items`,
                icon: 'pi pi-wrench',
                tone: pmCompliance >= 90 ? 'success' : pmCompliance >= 75 ? 'warn' : 'danger',
                progress: pmCompliance
            },
            {
                label: 'Rental Exposure',
                value: this.percentLabel(rentalShare),
                detail: `${this.countVehiclesBySource('Rental')} rented vehicles`,
                icon: 'pi pi-wallet',
                tone: rentalShare > 35 ? 'warn' : 'info',
                progress: rentalShare
            },
            {
                label: 'GPS Visibility',
                value: this.percentLabel(telematics),
                detail: `${this.onlineVehicleCount()} online trackers`,
                icon: 'pi pi-map-marker',
                tone: telematics >= 90 ? 'success' : telematics >= 60 ? 'info' : 'warn',
                progress: telematics
            }
        ];
    }

    readinessItems(): ReadinessItem[] {
        return [
            {
                label: 'Dispatchable Assets',
                value: `${this.dispatchableVehicleCount()} / ${this.vehicleList.length}`,
                detail: 'Availability, document, and active-status checks',
                severity: this.dispatchableVehicleCount() === this.vehicleList.length ? 'success' : 'warn'
            },
            {
                label: 'Active Mobilizations',
                value: this.activeMobilizations().length,
                detail: 'Drivers and vehicles deployed to projects or sites',
                severity: this.activeMobilizations().length > 0 ? 'success' : 'info'
            },
            {
                label: 'Pending Approval',
                value: this.countMobilizationsByStatus('SUBMITTED'),
                detail: 'Submitted mobilizations waiting for release',
                severity: this.countMobilizationsByStatus('SUBMITTED') > 0 ? 'warn' : 'success'
            },
            {
                label: 'Document Expiries',
                value: this.expiringDocumentCount(),
                detail: 'Vehicle and driver documents due within 30 days',
                severity: this.expiringDocumentCount() > 0 ? 'danger' : 'success'
            },
            {
                label: 'Parts Constraint',
                value: this.waitingPartsCount(),
                detail: 'Work orders waiting on stock or vendor supply',
                severity: this.waitingPartsCount() > 0 ? 'warn' : 'success'
            }
        ];
    }

    availabilityMetrics(): { label: string; value: string; detail: string; tone: FleetTone; icon: string }[] {
        const summary = this.availability?.summary;
        const driverUtilization = summary?.averageDriverUtilization ?? 0;
        const vehicleUtilization = summary?.averageVehicleUtilization ?? 0;
        return [
            {
                label: 'Idle Drivers',
                value: `${summary?.freeDrivers ?? 0}`,
                detail: `${summary?.busyDrivers ?? 0} busy now`,
                tone: (summary?.freeDrivers ?? 0) > 0 ? 'success' : 'warn',
                icon: 'pi pi-user'
            },
            {
                label: 'Idle Trucks',
                value: `${summary?.freeVehicles ?? 0}`,
                detail: `${summary?.busyVehicles ?? 0} busy or blocked`,
                tone: (summary?.freeVehicles ?? 0) > 0 ? 'success' : 'warn',
                icon: 'pi pi-truck'
            },
            {
                label: 'Driver Utilization',
                value: this.percentLabel(driverUtilization),
                detail: 'Next 7 days, 24h/day base',
                tone: driverUtilization >= 80 ? 'warn' : driverUtilization >= 45 ? 'info' : 'success',
                icon: 'pi pi-chart-line'
            },
            {
                label: 'Truck Utilization',
                value: this.percentLabel(vehicleUtilization),
                detail: 'Deliveries and project-backed mobilizations',
                tone: vehicleUtilization >= 80 ? 'warn' : vehicleUtilization >= 45 ? 'info' : 'success',
                icon: 'pi pi-chart-bar'
            }
        ];
    }

    priorityAvailabilityRows(): FleetAvailabilityRow[] {
        const drivers = this.availability?.driverRows ?? [];
        const vehicles = this.availability?.vehicleRows ?? [];
        return [...drivers, ...vehicles]
            .filter(row => (row.availabilityStatus || '').toUpperCase() !== 'FREE')
            .slice(0, 6);
    }

    availabilityStatusLabel(status?: string): string {
        switch ((status || '').toUpperCase()) {
            case 'FREE':
                return 'Idle';
            case 'BUSY':
                return 'Busy';
            case 'MAINTENANCE_BLOCKED':
                return 'Blocked';
            default:
                return status || '-';
        }
    }

    availabilityStatusClass(status?: string): string {
        switch ((status || '').toUpperCase()) {
            case 'FREE':
                return 'availability-tag-idle';
            case 'BUSY':
                return 'availability-tag-busy';
            case 'MAINTENANCE_BLOCKED':
                return 'availability-tag-blocked';
            default:
                return 'availability-tag-neutral';
        }
    }

    availabilityLocationLabel(row: FleetAvailabilityRow): string {
        return [row.availableCityName, row.availableCountryName].filter(Boolean).join(', ')
            || row.availableLocationLabel
            || 'Location not available';
    }

    availabilityWorkLabel(row: FleetAvailabilityRow): string {
        return row.currentWork?.context || row.availabilityReason || 'No active work';
    }

    availabilityNextFreeLabel(row: FleetAvailabilityRow): string {
        if ((row.availabilityStatus || '').toUpperCase() === 'FREE') {
            return 'Now';
        }
        return this.dateTimeLabel(row.nextFreeAt);
    }

    availabilityUtilizationLabel(row: FleetAvailabilityRow): string {
        return this.percentLabel(row.utilizationPercent ?? 0);
    }

    openAvailabilityCockpit(): void {
        this.router.navigate(['/fleet/mobilization/planning'], { queryParams: { tab: 'availability' } });
    }

    openAvailabilityRow(row: FleetAvailabilityRow): void {
        const work = row.currentWork;
        if (work?.deliveryId) {
            this.router.navigate(['/shipment/delivery/view', work.deliveryId]);
            return;
        }
        if (work?.mobilizationId) {
            this.router.navigate(['/fleet/mobilization', work.mobilizationId]);
            return;
        }
        if (row.resourceType === 'DRIVER' && row.resourceId) {
            this.router.navigate(['/fleet/driver/view', row.resourceId]);
            return;
        }
        if (row.resourceType === 'VEHICLE' && row.resourceId) {
            this.router.navigate(['/fleet/vehicle/view', row.resourceId]);
        }
    }

    loadVehicleTable(event?: TableLazyLoadEvent): void {
        this.vehicleTableFirst = event?.first ?? this.vehicleTableFirst;
        this.vehicleTableRowsPerPage = event?.rows ?? this.vehicleTableRowsPerPage;
        const rows = this.filteredVehicleRows();
        this.vehicleTableTotalRecords = rows.length;
        this.vehicleTableRows = rows.slice(this.vehicleTableFirst, this.vehicleTableFirst + this.vehicleTableRowsPerPage);
    }

    loadDriverTable(event?: TableLazyLoadEvent): void {
        this.driverTableFirst = event?.first ?? this.driverTableFirst;
        this.driverTableRowsPerPage = event?.rows ?? this.driverTableRowsPerPage;
        const rows = this.filteredDriverRows();
        this.driverTableTotalRecords = rows.length;
        this.driverTableRows = rows.slice(this.driverTableFirst, this.driverTableFirst + this.driverTableRowsPerPage);
    }

    applyVehicleTableFilters(): void {
        this.vehicleTableFirst = 0;
        this.loadVehicleTable({ first: 0, rows: this.vehicleTableRowsPerPage });
    }

    applyDriverTableFilters(): void {
        this.driverTableFirst = 0;
        this.loadDriverTable({ first: 0, rows: this.driverTableRowsPerPage });
    }

    clearVehicleTableFilters(): void {
        this.vehicleTableSearch = '';
        this.vehicleSourceFilter = null;
        this.vehicleStateFilter = null;
        this.applyVehicleTableFilters();
    }

    clearDriverTableFilters(): void {
        this.driverTableSearch = '';
        this.driverSourceFilter = null;
        this.driverDocumentFilter = null;
        this.applyDriverTableFilters();
    }

    activeMobilizations(): Mobilization[] {
        return this.mobilizations
            .filter(item => ['ACTIVE', 'APPROVED', 'SUBMITTED'].includes(item.status || ''))
            .sort((a, b) => this.statusRank(a.status) - this.statusRank(b.status))
            .slice(0, 10);
    }

    maintenanceQueueRows(): MaintenanceQueueItem[] {
        const alertRows = this.maintenanceAlerts.map(alert => ({
            type: alert.type || 'ALERT',
            title: alert.title || alert.message || 'Maintenance alert',
            asset: alert.vehiclePlateNumber || '-',
            owner: alert.status || '-',
            status: alert.severity || 'INFO',
            severity: this.statusSeverity(alert.severity),
            actionUrl: alert.actionUrl
        }));
        const workOrderRows = this.workOrders
            .filter(row => !['COMPLETED', 'COST_POSTED', 'CANCELLED', 'REJECTED'].includes(row.status || ''))
            .map(row => ({
                type: 'WORK_ORDER',
                title: row.title || row.workOrderNumber || 'Work order',
                asset: row.vehiclePlateNumber || '-',
                owner: row.vendorName || row.shopName || row.driverName || '-',
                status: row.status || 'OPEN',
                severity: this.statusSeverity(row.status),
                workOrderId: row.workOrderId
            }));
        return [...alertRows, ...workOrderRows].slice(0, 12);
    }

    openVehicle(vehicle: FleetVehicleRow): void {
        if (vehicle.vehicleId) {
            this.router.navigate(['/fleet/vehicle/view', vehicle.vehicleId]);
        }
    }

    trackVehicle(vehicle: FleetVehicleRow): void {
        if (vehicle.vehicleId) {
            this.router.navigate(['/fleet/gps/vehicles', vehicle.vehicleId]);
        }
    }

    openDriver(driver: FleetDriverRow): void {
        if (driver.id) {
            this.router.navigate(['/fleet/driver/view', driver.id]);
        }
    }

    openMobilization(item: Mobilization): void {
        if (item.mobilizationId) {
            this.router.navigate(['/fleet/mobilization', item.mobilizationId]);
        }
    }

    openMaintenance(row: MaintenanceQueueItem): void {
        if (row.actionUrl) {
            this.router.navigateByUrl(row.actionUrl);
            return;
        }
        if (row.workOrderId) {
            this.router.navigate(['/fleet/maintenance/work-orders', row.workOrderId]);
        }
    }

    openInfo(vehicle: FleetVehicleRow): void {
        this.selectTrackingVehicle(vehicle);
    }

    selectTrackingVehicle(vehicle: FleetVehicleRow): void {
        if (!vehicle?.vehicleId) {
            return;
        }
        if (this.selectedTrackingVehicle?.vehicleId !== vehicle.vehicleId) {
            this.replayMarkerAnimator.snapTo(null);
        }
        this.selectedTrackingVehicle = vehicle;
        this.selectedTrackingLive = this.gpsLiveVehicles.find(item => item.vehicleId === vehicle.vehicleId)
            ?? this.selectedTrackingLive;
        if (vehicle.currentLat && vehicle.currentLng) {
            this.center = { lat: vehicle.currentLat, lng: vehicle.currentLng };
            this.zoom = 14;
        }
        this.loadSelectedVehicleGpsDetail(vehicle.vehicleId);
    }

    openTrackingHistory(vehicle: FleetVehicleRow): void {
        if (vehicle.vehicleId) {
            this.router.navigate(['/fleet/gps/vehicles', vehicle.vehicleId, 'history'], {
                queryParams: this.selectedGpsSourceKey ? { sourceKey: this.selectedGpsSourceKey } : {}
            });
        }
    }

    onGpsSourceChange(): void {
        this.replayMarkerAnimator.snapTo(null);
        this.loadTelematics();
    }

    onGpsHistoryRangeChange(): void {
        if (this.selectedTrackingVehicle?.vehicleId) {
            this.loadSelectedVehicleGpsDetail(this.selectedTrackingVehicle.vehicleId);
        }
    }

    toggleGpsPolling(): void {
        this.gpsPollingEnabled = !this.gpsPollingEnabled;
        this.configureGpsPolling();
    }

    gpsSourceOptions(): GpsSelectOption[] {
        return [
            { label: 'Best available', value: '' },
            ...this.gpsSources.map(source => ({
                label: this.displayGpsName(source.displayName),
                value: source.sourceKey
            }))
        ];
    }

    gpsSourceName(sourceKey?: string | null): string {
        if (!sourceKey) {
            return 'Best available';
        }
        const source = this.gpsSources.find(item => item.sourceKey === sourceKey);
        return this.displayGpsName(source?.displayName ?? sourceKey);
    }

    gpsStatusLabel(status?: string | null): string {
        switch (status) {
            case 'ACTIVE_LOCATION': return 'Active';
            case 'STALE_LOCATION': return 'Stale';
            case 'NO_GPS_MAPPING': return 'No mapping';
            case 'NO_LOCATION': return 'No location';
            default: return status || 'Unavailable';
        }
    }

    gpsStatusSeverity(status?: string | null): FleetTone {
        switch (status) {
            case 'ACTIVE_LOCATION': return 'success';
            case 'STALE_LOCATION': return 'warn';
            case 'NO_GPS_MAPPING':
            case 'NO_LOCATION': return 'danger';
            default: return 'secondary';
        }
    }

    gpsEventSeverity(eventType?: string | null): FleetTone {
        switch (eventType) {
            case 'GPS_SIGNAL_LOST': return 'danger';
            case 'GPS_SIGNAL_RESTORED':
            case 'GPS_SIGNAL_ACQUIRED': return 'success';
            case 'MOVING':
            case 'IGNITION_ON': return 'info';
            case 'STOPPED':
            case 'IGNITION_OFF': return 'secondary';
            default: return 'warn';
        }
    }

    gpsCoordinateLabel(location?: { latitude?: number | string | null; longitude?: number | string | null } | null): string {
        const lat = Number(location?.latitude);
        const lng = Number(location?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            return '-';
        }
        return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    }

    gpsSpeedLabel(location?: { speedKph?: number | string | null } | null): string {
        return `${Number(location?.speedKph ?? 0).toFixed(1)} kph`;
    }

    gpsDateLabel(value?: string | null): string {
        return value ? formatDate(value, 'dd MMM yyyy, HH:mm', 'en-US') : '-';
    }

    gpsRoutePath(): google.maps.LatLngLiteral[] {
        return this.gpsHistory
            .map(point => this.gpsPointPosition(point))
            .filter((position): position is google.maps.LatLngLiteral => position !== null);
    }

    replayMarkerPosition(): google.maps.LatLngLiteral | null {
        return this.currentReplayPoint ? this.replayMarkerAnimator.position() : null;
    }

    vehicleMarkerOptions(vehicle?: FleetVehicleRow, selected = false, replay = false): google.maps.MarkerOptions {
        if (replay) {
            return this.truckMarkerOptions(this.replayMarkerAnimator.headingDegrees(), true, false, 40);
        }
        const live = vehicle ? this.gpsLiveForVehicle(vehicle) : undefined;
        const heading = vehicle && live
            ? this.vehicleMarkerAnimators.get(this.vehicleMarkerKey(vehicle, live))?.headingDegrees() ?? live.location?.headingDegrees
            : 0;
        return this.truckMarkerOptions(heading, selected, live?.liveStatus === 'STALE_LOCATION', selected ? 40 : 22);
    }

    startReplay(): void {
        if (this.gpsHistory.length < 2) {
            return;
        }
        if (this.replayIndex >= this.gpsHistory.length - 1) {
            this.setReplayIndex(0, true);
        }
        this.replayPlaying = true;
        this.scheduleReplayStep();
    }

    pauseReplay(): void {
        this.replayPlaying = false;
        if (this.replayTimer) {
            clearTimeout(this.replayTimer);
            this.replayTimer = undefined;
        }
    }

    resetReplay(shouldCenter = true): void {
        this.pauseReplay();
        this.setReplayIndex(0, shouldCenter);
    }

    onReplayIndexChange(value: string | number): void {
        this.pauseReplay();
        this.setReplayIndex(Number(value), true);
    }

    onReplaySpeedChange(): void {
        if (this.replayPlaying) {
            this.scheduleReplayStep();
        }
    }

    replayProgress(): number {
        if (this.gpsHistory.length <= 1) {
            return this.gpsHistory.length ? 100 : 0;
        }
        return Math.round((this.replayIndex / (this.gpsHistory.length - 1)) * 100);
    }

    replayMaxIndex(): number {
        return Math.max(this.gpsHistory.length - 1, 0);
    }

    derivedGpsAlerts(): GpsVehicleEvent[] {
        return this.gpsEvents.filter(event => this.derivedAlertEventTypes.has(event.eventType));
    }

    sourceSeverity(source?: FleetAssetSource): FleetTone {
        if (source === 'Owned') {
            return 'info';
        }
        if (source === 'Rental') {
            return 'warn';
        }
        if (source === 'Transporter') {
            return 'secondary';
        }
        return 'success';
    }

    statusSeverity(status?: string): FleetTone {
        const value = (status || '').toUpperCase();
        if (['AVAILABLE', 'ACTIVE', 'COMPLETED', 'VALID', 'ONLINE', 'SUCCESS'].includes(value)) {
            return 'success';
        }
        if (['OUT_OF_SERVICE', 'OVERDUE', 'EXPIRED', 'CRITICAL', 'HIGH', 'REJECTED', 'CANCELLED', 'BLOCKED'].includes(value)) {
            return 'danger';
        }
        if (['MAINTENANCE_REVIEW', 'WAITING_PARTS', 'WAITING_VENDOR', 'DUE', 'DUE_SOON', 'SUBMITTED', 'APPROVED', 'WARN', 'WARNING'].includes(value)) {
            return 'warn';
        }
        if (!value) {
            return 'secondary';
        }
        return 'info';
    }

    motionLabel(vehicle: FleetVehicleRow): string {
        if (vehicle.gpsStatus !== 'Online') {
            return 'Offline';
        }
        return vehicle.motion ? 'Moving' : 'Idle';
    }

    vehicleDocumentStatus(vehicle: FleetVehicleRow): string {
        const registrationDays = this.daysUntil(vehicle.registrationExpiry);
        const insuranceDays = this.daysUntil(vehicle.insuranceExpiry);
        if (registrationDays === null || insuranceDays === null) {
            return 'Missing';
        }
        if (registrationDays < 0 || insuranceDays < 0) {
            return 'Expired';
        }
        if (registrationDays <= 30 || insuranceDays <= 30) {
            return 'Due Soon';
        }
        return 'Valid';
    }

    driverDocumentStatus(driver: FleetDriverRow): string {
        const licenseDays = this.daysUntil(driver.licenseExpiryDate);
        const residenceDays = this.daysUntil(driver.residenceExpiryDate);
        if (licenseDays === null || residenceDays === null) {
            return 'Missing';
        }
        if (licenseDays < 0 || residenceDays < 0) {
            return 'Expired';
        }
        if (licenseDays <= 30 || residenceDays <= 30) {
            return 'Due Soon';
        }
        return 'Valid';
    }

    dateLabel(value?: string | Date | number | null): string {
        const date = this.toDate(value);
        return date ? formatDate(date, 'MMM d, y', 'en-US') : '-';
    }

    dateTimeLabel(value?: string | Date | number | null): string {
        const date = this.toDate(value);
        return date ? formatDate(date, 'MMM d, y HH:mm', 'en-US') : '-';
    }

    money(value?: number | null): string {
        return value === undefined || value === null ? '-' : `${this.moneyFormatter.format(value)} SAR`;
    }

    driverName(driver?: Driver | null): string {
        return driver?.legalName || driver?.name || driver?.phoneNumber || '-';
    }

    projectLabel(item: Mobilization): string {
        if (item.projectSource === 'LOGISTICS_PROJECT') {
            return item.logisticsProjectId ? `Logistics Project #${item.logisticsProjectId}` : 'Logistics Project';
        }
        return [item.manualProjectCode, item.manualProjectName].filter(Boolean).join(' - ') || 'Manual project';
    }

    siteLabel(item: Mobilization): string {
        return item.manualSiteName || item.manualSiteAddress || '-';
    }

    private filteredVehicleRows(): FleetVehicleRow[] {
        const keyword = this.normalizeFilter(this.vehicleTableSearch);
        return [...this.vehicleList]
            .filter(vehicle => !this.vehicleSourceFilter || vehicle.source === this.vehicleSourceFilter)
            .filter(vehicle => {
                if (!this.vehicleStateFilter) {
                    return true;
                }
                return this.motionLabel(vehicle) === this.vehicleStateFilter
                    || this.vehicleDocumentStatus(vehicle) === this.vehicleStateFilter
                    || vehicle.maintenanceStatus === this.vehicleStateFilter;
            })
            .filter(vehicle => !keyword || this.containsFilter(keyword, [
                vehicle.plateNumber,
                vehicle.registrationNumber,
                vehicle.availabilityReason,
                vehicle.maintenanceStatus,
                this.motionLabel(vehicle),
                this.vehicleDocumentStatus(vehicle),
                this.driverName(vehicle.currentDriver),
                vehicle.currentDriver?.phoneNumber,
                vehicle.source
            ]))
            .sort((a, b) => this.vehicleRiskScore(b) - this.vehicleRiskScore(a));
    }

    private filteredDriverRows(): FleetDriverRow[] {
        const keyword = this.normalizeFilter(this.driverTableSearch);
        return [...this.driverList]
            .filter(driver => !this.driverSourceFilter || driver.source === this.driverSourceFilter)
            .filter(driver => !this.driverDocumentFilter || this.driverDocumentStatus(driver) === this.driverDocumentFilter)
            .filter(driver => !keyword || this.containsFilter(keyword, [
                this.driverName(driver),
                driver.phoneNumber,
                driver.emailAddress,
                driver.licenseNumber,
                driver.residenceNumber,
                driver.currentVehicle?.plateNumber,
                driver.currentVehicle?.registrationNumber,
                this.driverDocumentStatus(driver),
                driver.source
            ]))
            .sort((a, b) => this.driverRiskScore(b) - this.driverRiskScore(a));
    }

    private containsFilter(keyword: string, values: Array<string | number | null | undefined>): boolean {
        return values.some(value => this.normalizeFilter(value).includes(keyword));
    }

    private normalizeFilter(value: string | number | null | undefined): string {
        return String(value ?? '').trim().toLowerCase();
    }

    private safeList<T>(request: Observable<APIListWrapper<T>> | null): Observable<APIListWrapper<T>> {
        if (!request) {
            return of(this.emptyList<T>());
        }
        return request.pipe(catchError(() => of(this.emptyList<T>())));
    }

    private safeResponse<T>(request: Observable<APIResponseWrapper<T>>): Observable<T | undefined> {
        return request.pipe(
            map(response => response.data),
            catchError(() => of(undefined))
        );
    }

    private emptyList<T>(): APIListWrapper<T> {
        return { items: [], count: 0, hasMore: false, limit: 0, offset: 0 };
    }

    private toFleetVehicles(vehicles: VehicleMin[] = [], source: FleetAssetSource): FleetVehicleRow[] {
        return vehicles.map(vehicle => ({
            ...vehicle,
            source,
            gpsStatus: 'Offline',
            currentLat: vehicle.currentLat ?? this.center.lat,
            currentLng: vehicle.currentLng ?? this.center.lng
        }));
    }

    private toFleetDrivers(drivers: Driver[] = [], source: FleetAssetSource): FleetDriverRow[] {
        return drivers.map(driver => ({ ...driver, source }));
    }

    private transporterFleetResources(): Observable<{ vehicles: VehicleMin[]; drivers: Driver[] }> {
        return this.crmService.getScopedTransporters(this.companyId).pipe(
            switchMap(transporters => {
                if (!transporters?.length) {
                    return of({ vehicles: [], drivers: [] });
                }
                return forkJoin(transporters.map(transporter => forkJoin({
                    vehicles: this.safeList<any>(
                        this.vehicleService.getTransporterVehicles(0, this.pageSize, this.companyId, transporter.crmClientId)
                    ),
                    drivers: this.safeList(this.driverService.getTransporterDrivers(0, this.pageSize, this.companyId, transporter.crmClientId))
                }))).pipe(
                    map(scoped => ({
                        vehicles: scoped.flatMap(entry => entry.vehicles.items ?? []),
                        drivers: scoped.flatMap(entry => entry.drivers.items ?? [])
                    }))
                );
            }),
            catchError(() => of({ vehicles: [], drivers: [] }))
        );
    }

    loadTelematics(showSpinner = true): void {
        if (!this.companyId) {
            return;
        }

        if (showSpinner) {
            this.isTelematicsLoading = true;
        }
        forkJoin({
            sources: this.gpsTrackingService.getTrackingSources(this.companyId).pipe(catchError(() => of([]))),
            vehicles: this.gpsTrackingService.getCompanyVehicleLive(this.companyId, this.selectedGpsSourceKey).pipe(catchError(() => of([])))
        }).pipe(finalize(() => this.isTelematicsLoading = false)).subscribe(result => {
            this.gpsSources = result.sources;
            this.gpsLiveVehicles = result.vehicles;
            this.applyGpsLiveVehicles(result.vehicles);
            this.buildCharts();
            this.loadVehicleTable({ first: this.vehicleTableFirst, rows: this.vehicleTableRowsPerPage });
            this.ensureSelectedTrackingVehicle(showSpinner);
            this.configureGpsPolling();
        });
    }

    private applyGpsLiveVehicles(liveVehicles: GpsVehicleLive[]): void {
        this.vehicleList.forEach(vehicle => {
            vehicle.gpsStatus = 'Offline';
            vehicle.motion = false;
            vehicle.speed = 0;
        });
        const rowsByVehicleId = new Map(this.vehicleList.map(vehicle => [Number(vehicle.vehicleId), vehicle]));
        liveVehicles.forEach(live => {
            const row = rowsByVehicleId.get(Number(live.vehicleId));
            if (!row) {
                return;
            }
            const position = this.gpsLocationPosition(live.location);
            if (!position) {
                row.gpsStatus = 'Offline';
                return;
            }
            row.currentLat = position.lat;
            row.currentLng = position.lng;
            row.speed = Number(live.location?.speedKph ?? 0);
            row.motion = Number(live.location?.speedKph ?? 0) >= 5;
            row.gpsStatus = live.liveStatus === 'ACTIVE_LOCATION' ? 'Online' : 'Offline';
        });
        this.syncVehicleMarkerAnimations();
    }

    private ensureSelectedTrackingVehicle(resetReplay = true): void {
        const selectedId = this.selectedTrackingVehicle?.vehicleId;
        const preferredLive = selectedId
            ? this.gpsLiveVehicles.find(live => live.vehicleId === selectedId)
            : this.gpsLiveVehicles.find(live => live.liveStatus === 'ACTIVE_LOCATION') ?? this.gpsLiveVehicles[0];
        const preferredVehicle = preferredLive
            ? this.vehicleList.find(vehicle => vehicle.vehicleId === preferredLive.vehicleId)
            : this.vehicleList.find(vehicle => vehicle.gpsStatus === 'Online') ?? this.vehicleList[0];
        if (preferredVehicle) {
            this.selectedTrackingVehicle = preferredVehicle;
            this.selectedTrackingLive = preferredLive ?? this.gpsLiveVehicles.find(live => live.vehicleId === preferredVehicle.vehicleId);
            this.loadSelectedVehicleGpsDetail(preferredVehicle.vehicleId, resetReplay);
        }
    }

    private loadSelectedVehicleGpsDetail(vehicleId: number, resetReplay = true): void {
        if (!this.companyId || !vehicleId) {
            return;
        }
        if (resetReplay) {
            this.pauseReplay();
        }
        const from = this.gpsHistoryRange?.[0];
        const to = this.gpsHistoryRange?.[1];
        forkJoin({
            live: this.gpsTrackingService.getVehicleLive(this.companyId, vehicleId, this.selectedGpsSourceKey).pipe(catchError(() => of(undefined))),
            events: this.gpsTrackingService.getVehicleEvents(this.companyId, vehicleId, this.selectedGpsSourceKey, from, to, 500).pipe(catchError(() => of([]))),
            history: this.gpsTrackingService.getVehicleHistory(this.companyId, vehicleId, this.selectedGpsSourceKey, from, to).pipe(catchError(() => of([])))
        }).subscribe(result => {
            this.selectedTrackingLive = result.live;
            this.gpsEvents = this.sortGpsEvents(result.events);
            this.gpsHistory = this.sortGpsHistory(result.history);
            if (resetReplay) {
                this.resetReplay(false);
            } else {
                this.syncReplayPointAfterHistoryRefresh();
            }
            const position = this.gpsPointPosition(this.currentReplayPoint) || this.gpsLocationPosition(result.live?.location);
            if (position && resetReplay) {
                this.center = position;
                this.zoom = 14;
            }
        });
    }

    private configureGpsPolling(): void {
        this.gpsPollSub?.unsubscribe();
        if (!this.gpsPollingEnabled || !this.companyId) {
            return;
        }
        this.gpsPollSub = interval(15000).subscribe(() => {
            if (!document.hidden) {
                this.loadTelematics(false);
            }
        });
    }

    private gpsLocationPosition(location?: GpsLocation | null): google.maps.LatLngLiteral | null {
        const lat = Number(location?.latitude);
        const lng = Number(location?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            return null;
        }
        return { lat, lng };
    }

    gpsPointPosition(point?: GpsLocationHistoryPoint | null): google.maps.LatLngLiteral | null {
        const lat = Number(point?.latitude);
        const lng = Number(point?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            return null;
        }
        return { lat, lng };
    }

    private setReplayIndex(index: number, shouldCenter: boolean, transitionMs = 700): void {
        if (!this.gpsHistory.length) {
            this.replayIndex = 0;
            this.currentReplayPoint = undefined;
            this.replayMarkerAnimator.snapTo(null);
            return;
        }
        const nextIndex = Math.min(Math.max(Math.trunc(index) || 0, 0), this.gpsHistory.length - 1);
        this.replayIndex = nextIndex;
        this.currentReplayPoint = this.gpsHistory[nextIndex];
        const position = this.gpsPointPosition(this.currentReplayPoint);
        this.moveReplayMarker(position, shouldCenter, transitionMs, this.currentReplayPoint);
        if (shouldCenter && position) {
            this.zoom = 14;
        }
    }

    private scheduleReplayStep(): void {
        if (this.replayTimer) {
            clearTimeout(this.replayTimer);
            this.replayTimer = undefined;
        }
        if (!this.replayPlaying || this.replayIndex >= this.gpsHistory.length - 1) {
            this.pauseReplay();
            return;
        }
        const delay = this.replayDelayMs(this.gpsHistory[this.replayIndex], this.gpsHistory[this.replayIndex + 1]);
        this.replayTimer = setTimeout(() => {
            this.setReplayIndex(this.replayIndex + 1, true, Math.min(Math.max(delay * 0.85, 80), 700));
            this.scheduleReplayStep();
        }, delay);
    }

    animatedVehicleMarkerPosition(vehicle: FleetVehicleRow): google.maps.LatLngLiteral {
        const fallback = { lat: vehicle.currentLat ?? this.center.lat, lng: vehicle.currentLng ?? this.center.lng };
        const live = this.gpsLiveForVehicle(vehicle);
        return live
            ? this.vehicleMarkerAnimators.get(this.vehicleMarkerKey(vehicle, live))?.position() ?? fallback
            : fallback;
    }

    private syncVehicleMarkerAnimations(): void {
        const rowsByVehicleId = new Map(this.vehicleList.map(vehicle => [Number(vehicle.vehicleId), vehicle]));
        const activeKeys = new Set<string>();
        this.gpsLiveVehicles.forEach(live => {
            const vehicle = rowsByVehicleId.get(Number(live.vehicleId));
            if (!vehicle) {
                return;
            }
            const key = this.vehicleMarkerKey(vehicle, live);
            activeKeys.add(key);
            this.vehicleMarkerAnimator(vehicle, live).moveTo(
                this.gpsLocationPosition(live.location),
                live.location?.headingDegrees,
                live.location?.speedKph
            );
        });
        this.vehicleMarkerAnimators.forEach((animator, key) => {
            if (!activeKeys.has(key)) {
                animator.destroy();
                this.vehicleMarkerAnimators.delete(key);
            }
        });
    }

    private vehicleMarkerAnimator(vehicle: FleetVehicleRow, live: GpsVehicleLive): GpsMarkerAnimator {
        const key = this.vehicleMarkerKey(vehicle, live);
        let animator = this.vehicleMarkerAnimators.get(key);
        if (!animator) {
            animator = new GpsMarkerAnimator();
            this.vehicleMarkerAnimators.set(key, animator);
        }
        return animator;
    }

    private gpsLiveForVehicle(vehicle: FleetVehicleRow): GpsVehicleLive | undefined {
        return this.gpsLiveVehicles.find(live => Number(live.vehicleId) === Number(vehicle.vehicleId)
            && (!this.selectedGpsSourceKey || live.sourceKey === this.selectedGpsSourceKey));
    }

    private vehicleMarkerKey(vehicle: FleetVehicleRow, live: GpsVehicleLive): string {
        return `${vehicle.vehicleId}:${live.sourceKey || this.selectedGpsSourceKey || 'best'}`;
    }

    private moveReplayMarker(
        position: google.maps.LatLngLiteral | null,
        shouldCenter: boolean,
        transitionMs = 700,
        sample?: GpsLocationHistoryPoint | null
    ): void {
        this.followReplayMarker = shouldCenter;
        this.replayMarkerAnimator.moveTo(position, sample?.headingDegrees, sample?.speedKph, transitionMs);
    }

    private syncReplayPointAfterHistoryRefresh(): void {
        if (!this.gpsHistory.length) {
            this.pauseReplay();
            this.setReplayIndex(0, false);
            return;
        }
        const at = this.currentReplayPoint?.recordedAt;
        const matching = at ? this.gpsHistory.findIndex(point => point.recordedAt === at) : -1;
        this.setReplayIndex(matching >= 0 ? matching : Math.min(this.replayIndex, this.gpsHistory.length - 1), false);
    }

    private replayDelayMs(current?: GpsLocationHistoryPoint, next?: GpsLocationHistoryPoint): number {
        const fallback = 1000 / Math.max(this.replaySpeed, 1);
        const currentTime = current?.recordedAt ? new Date(current.recordedAt).getTime() : NaN;
        const nextTime = next?.recordedAt ? new Date(next.recordedAt).getTime() : NaN;
        const delta = Number.isFinite(currentTime) && Number.isFinite(nextTime) && nextTime > currentTime
            ? nextTime - currentTime
            : fallback;
        return Math.min(Math.max(delta / Math.max(this.replaySpeed, 1), 120), 2000);
    }

    private sortGpsHistory(points: GpsLocationHistoryPoint[]): GpsLocationHistoryPoint[] {
        return [...points].sort((left, right) => this.gpsTimeValue(left.recordedAt) - this.gpsTimeValue(right.recordedAt));
    }

    private sortGpsEvents(events: GpsVehicleEvent[]): GpsVehicleEvent[] {
        return [...events].sort((left, right) => this.gpsTimeValue(right.eventTime) - this.gpsTimeValue(left.eventTime));
    }

    private gpsTimeValue(value?: string | null): number {
        const time = value ? new Date(value).getTime() : NaN;
        return Number.isFinite(time) ? time : 0;
    }

    private truckMarkerOptions(headingDegrees: number | string | null | undefined, selected: boolean, stale: boolean, zIndex: number): google.maps.MarkerOptions {
        return { icon: gpsTruckMarkerIcon({ headingDegrees, selected, stale }), zIndex, optimized: true };
    }

    private displayGpsName(value?: string | null): string {
        if (!value) {
            return '-';
        }
        return value
            .replace(/PROLOGIX/g, 'CARGOMATE')
            .replace(/ProLogix/g, 'CargoMate')
            .replace(/Prologix/g, 'CargoMate')
            .replace(/prologix/g, 'CargoMate');
    }

    private applyMaintenanceAvailability(): void {
        const availabilityByVehicle = new Map<number, MaintenanceVehicleAvailability>();
        const availabilityByPlate = new Map<string, MaintenanceVehicleAvailability>();
        this.maintenanceAvailability.forEach(item => {
            if (item.vehicleId) {
                availabilityByVehicle.set(item.vehicleId, item);
            }
            if (item.vehiclePlateNumber) {
                availabilityByPlate.set(item.vehiclePlateNumber, item);
            }
        });

        this.vehicleList.forEach(vehicle => {
            const availability = availabilityByVehicle.get(vehicle.vehicleId) || availabilityByPlate.get(vehicle.plateNumber);
            vehicle.maintenanceStatus = availability?.availabilityStatus || 'AVAILABLE';
            vehicle.availabilityReason = availability?.reason;
        });
    }

    private buildCharts(): void {
        this.assetStatusChartData = {
            labels: ['Moving', 'Idle', 'Offline', 'Unavailable'],
            datasets: [{
                data: [
                    this.vehicleList.filter(vehicle => vehicle.gpsStatus === 'Online' && vehicle.motion).length,
                    this.vehicleList.filter(vehicle => vehicle.gpsStatus === 'Online' && !vehicle.motion).length,
                    this.vehicleList.filter(vehicle => vehicle.gpsStatus === 'Offline').length,
                    this.vehicleList.filter(vehicle => ['OUT_OF_SERVICE', 'BLOCKED'].includes(vehicle.maintenanceStatus || '')).length
                ],
                backgroundColor: ['#10b981', '#0ea5e9', '#94a3b8', '#ef4444'],
                borderWidth: 0
            }]
        };

        this.ownershipChartData = {
            labels: ['Owned', 'Freelance', 'Rental', 'Transporter'],
            datasets: [{
                data: [
                    this.countVehiclesBySource('Owned'),
                    this.countVehiclesBySource('Freelance'),
                    this.countVehiclesBySource('Rental'),
                    this.countVehiclesBySource('Transporter')
                ],
                backgroundColor: ['#0f766e', '#2563eb', '#f59e0b', '#64748b'],
                borderWidth: 0
            }]
        };

        this.mobilizationStatusChartData = {
            labels: ['Draft', 'Submitted', 'Approved', 'Active', 'Completed', 'Rejected'],
            datasets: [{
                label: 'Mobilizations',
                data: [
                    this.countMobilizationsByStatus('DRAFT'),
                    this.countMobilizationsByStatus('SUBMITTED'),
                    this.countMobilizationsByStatus('APPROVED'),
                    this.countMobilizationsByStatus('ACTIVE'),
                    this.countMobilizationsByStatus('COMPLETED'),
                    this.countMobilizationsByStatus('REJECTED')
                ],
                backgroundColor: ['#94a3b8', '#f59e0b', '#0ea5e9', '#10b981', '#64748b', '#ef4444'],
                borderRadius: 6
            }]
        };

        const days = this.nextDays(14);
        this.mobilizationTrendChartData = {
            labels: days.map(day => formatDate(day, 'MMM d', 'en-US')),
            datasets: [
                {
                    label: 'Active assignments',
                    data: days.map(day => this.mobilizationsActiveOn(day)),
                    borderColor: '#0f766e',
                    backgroundColor: 'rgba(15, 118, 110, 0.12)',
                    tension: 0.35,
                    fill: true
                },
                {
                    label: 'Dispatchable assets',
                    data: days.map(() => this.dispatchableVehicleCount()),
                    borderColor: '#2563eb',
                    backgroundColor: 'rgba(37, 99, 235, 0.08)',
                    tension: 0.35,
                    fill: false
                }
            ]
        };

        this.maintenanceCostChartData = {
            labels: ['Labor', 'Parts', 'External', 'Variance'],
            datasets: [{
                label: 'SAR',
                data: [
                    this.maintenanceAnalytics?.totalLaborCost ?? 0,
                    this.maintenanceAnalytics?.totalPartsCost ?? 0,
                    this.maintenanceAnalytics?.totalExternalCost ?? 0,
                    this.maintenanceAnalytics?.costVariance ?? 0
                ],
                backgroundColor: ['#2563eb', '#0f766e', '#f59e0b', '#ef4444'],
                borderRadius: 6
            }]
        };

        const downtimeRows = (this.maintenanceAnalytics?.downtimeByVehicle || []).slice(0, 8);
        this.maintenanceDowntimeChartData = {
            labels: downtimeRows.length ? downtimeRows.map(row => row.vehiclePlateNumber || row.label || '-') : ['No downtime'],
            datasets: [{
                label: 'Hours',
                data: downtimeRows.length ? downtimeRows.map(row => row.downtimeHours || ((row.downtimeMinutes || 0) / 60)) : [0],
                backgroundColor: '#f97316',
                borderRadius: 6
            }]
        };

        this.driverReadinessRadarData = {
            labels: ['License', 'Residence', 'Verified', 'Assigned', 'Mobilized'],
            datasets: [{
                label: 'Driver readiness',
                data: [
                    this.driverLicensePercent(),
                    this.driverResidencePercent(),
                    this.driverVerificationPercent(),
                    this.driverAssignmentPercent(),
                    this.driverMobilizationPercent()
                ],
                borderColor: '#0f766e',
                backgroundColor: 'rgba(15, 118, 110, 0.18)',
                pointBackgroundColor: '#0f766e'
            }]
        };

        this.riskPolarChartData = {
            labels: ['Expiring docs', 'High alerts', 'Overdue PM', 'Waiting parts', 'Out of service'],
            datasets: [{
                data: [
                    this.expiringDocumentCount(),
                    this.highMaintenanceAlertCount(),
                    this.overduePmCount(),
                    this.waitingPartsCount(),
                    this.outOfServiceCount()
                ],
                backgroundColor: ['#f59e0b', '#ef4444', '#dc2626', '#f97316', '#7f1d1d'],
                borderWidth: 1
            }]
        };

        this.speedDistanceBubbleData = {
            datasets: (['Owned', 'Freelance', 'Rental', 'Transporter'] as FleetAssetSource[]).map(source => ({
                label: source,
                data: this.vehicleList
                    .filter(vehicle => vehicle.source === source)
                    .map(vehicle => ({
                        x: Number((vehicle.distance || 0).toFixed(2)),
                        y: Math.round(vehicle.speed || 0),
                        r: vehicle.motion ? 8 : 5,
                        plate: vehicle.plateNumber
                    })),
                backgroundColor: source === 'Owned'
                    ? 'rgba(15, 118, 110, 0.62)'
                    : source === 'Freelance'
                        ? 'rgba(37, 99, 235, 0.58)'
                        : source === 'Rental'
                            ? 'rgba(245, 158, 11, 0.62)'
                            : 'rgba(100, 116, 139, 0.62)',
                borderColor: source === 'Owned'
                    ? '#0f766e'
                    : source === 'Freelance'
                        ? '#2563eb'
                        : source === 'Rental'
                            ? '#f59e0b'
                            : '#64748b'
            }))
        };
    }

    private setCurrentLocation(): void {
        if (!navigator.geolocation) {
            return;
        }
        navigator.geolocation.getCurrentPosition(position => {
            this.center = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
            };
        });
    }

    private countVehiclesBySource(source: FleetAssetSource): number {
        return this.vehicleList.filter(vehicle => vehicle.source === source).length;
    }

    private countDriversBySource(source: FleetAssetSource): number {
        return this.driverList.filter(driver => driver.source === source).length;
    }

    private countMobilizationsByStatus(status: MobilizationStatus): number {
        return this.mobilizations.filter(item => item.status === status).length;
    }

    private dispatchableVehicleCount(): number {
        return this.vehicleList.filter(vehicle => this.isDispatchable(vehicle)).length;
    }

    private isDispatchable(vehicle: FleetVehicleRow): boolean {
        if (vehicle.isActive === false) {
            return false;
        }
        if (['OUT_OF_SERVICE', 'BLOCKED'].includes(vehicle.maintenanceStatus || '')) {
            return false;
        }
        return !['Expired', 'Missing'].includes(this.vehicleDocumentStatus(vehicle));
    }

    onlineVehicleCount(): number {
        return this.vehicleList.filter(vehicle => vehicle.gpsStatus === 'Online').length;
    }

    private assignedVehicleCount(): number {
        const mobilizedVehicleIds = new Set(this.activeMobilizations().map(item => item.vehicleId).filter((id): id is number => !!id));
        return this.vehicleList.filter(vehicle => !!vehicle.currentDriver || mobilizedVehicleIds.has(vehicle.vehicleId)).length;
    }

    private activeMobilizationVehicleCount(): number {
        return new Set(this.mobilizations
            .filter(item => item.status === 'ACTIVE')
            .map(item => item.vehicleId)
            .filter((id): id is number => !!id)).size;
    }

    private pmCompliancePercent(): number {
        if (this.maintenanceAnalytics?.pmCompliancePercent !== undefined && this.maintenanceAnalytics?.pmCompliancePercent !== null) {
            return this.clampPercent(this.maintenanceAnalytics.pmCompliancePercent);
        }
        const totalPm = (this.maintenanceDashboard?.dueSoonPmItems ?? 0)
            + (this.maintenanceDashboard?.duePmItems ?? 0)
            + (this.maintenanceDashboard?.overduePmItems ?? 0);
        if (!totalPm) {
            return 100;
        }
        return this.clampPercent(((totalPm - this.overduePmCount()) / totalPm) * 100);
    }

    private maintenanceRiskCount(): number {
        return this.highMaintenanceAlertCount()
            + this.overduePmCount()
            + this.outOfServiceCount()
            + (this.maintenanceDashboard?.openDefects ?? 0)
            + this.waitingPartsCount();
    }

    private highMaintenanceAlertCount(): number {
        return this.maintenanceAlerts.filter(alert => ['HIGH', 'CRITICAL', 'DANGER'].includes((alert.severity || '').toUpperCase())).length
            + (this.maintenanceAnalytics?.criticalAlertCount ?? 0);
    }

    private overduePmCount(): number {
        return this.maintenanceDashboard?.overduePmItems ?? this.maintenanceAnalytics?.overduePmCount ?? 0;
    }

    private waitingPartsCount(): number {
        return this.maintenanceDashboard?.waitingPartsWorkOrders
            ?? this.workOrders.filter(row => row.status === 'WAITING_PARTS').length;
    }

    private outOfServiceCount(): number {
        return this.maintenanceDashboard?.outOfServiceVehicles
            ?? this.maintenanceAvailability.filter(row => row.availabilityStatus === 'OUT_OF_SERVICE').length;
    }

    private totalDistanceCovered(): number {
        return Number(this.vehicleList
            .map(vehicle => vehicle.distance ?? 0)
            .reduce((a, b) => a + b, 0)
            .toFixed(2));
    }

    private driverCompliancePercent(): number {
        return this.percentValue(this.validDriverDocumentCount(), this.driverList.length);
    }

    private validDriverDocumentCount(): number {
        return this.driverList.filter(driver => this.driverDocumentStatus(driver) === 'Valid').length;
    }

    private driverLicensePercent(): number {
        return this.percentValue(this.driverList.filter(driver => (this.daysUntil(driver.licenseExpiryDate) ?? -1) > 30).length, this.driverList.length);
    }

    private driverResidencePercent(): number {
        return this.percentValue(this.driverList.filter(driver => (this.daysUntil(driver.residenceExpiryDate) ?? -1) > 30).length, this.driverList.length);
    }

    private driverVerificationPercent(): number {
        return this.percentValue(this.driverList.filter(driver => driver.validated || driver.isIDVerified).length, this.driverList.length);
    }

    private driverAssignmentPercent(): number {
        return this.percentValue(this.driverList.filter(driver => !!driver.currentVehicle).length, this.driverList.length);
    }

    private driverMobilizationPercent(): number {
        const activeDriverIds = new Set(this.mobilizations
            .filter(item => item.status === 'ACTIVE')
            .map(item => item.driverId)
            .filter((id): id is number => !!id));
        return this.percentValue(this.driverList.filter(driver => activeDriverIds.has(driver.id)).length, this.driverList.length);
    }

    private expiringDocumentCount(): number {
        return this.vehicleList.filter(vehicle => ['Expired', 'Missing', 'Due Soon'].includes(this.vehicleDocumentStatus(vehicle))).length
            + this.driverList.filter(driver => ['Expired', 'Missing', 'Due Soon'].includes(this.driverDocumentStatus(driver))).length;
    }

    private vehicleRiskScore(vehicle: FleetVehicleRow): number {
        let score = 0;
        if (vehicle.gpsStatus === 'Offline') {
            score += 2;
        }
        if (['OUT_OF_SERVICE', 'BLOCKED'].includes(vehicle.maintenanceStatus || '')) {
            score += 5;
        }
        if (vehicle.maintenanceStatus === 'MAINTENANCE_REVIEW') {
            score += 3;
        }
        if (this.vehicleDocumentStatus(vehicle) === 'Expired') {
            score += 4;
        }
        if (this.vehicleDocumentStatus(vehicle) === 'Due Soon') {
            score += 2;
        }
        if (!vehicle.currentDriver) {
            score += 1;
        }
        return score;
    }

    private driverRiskScore(driver: FleetDriverRow): number {
        let score = 0;
        if (this.driverDocumentStatus(driver) === 'Expired') {
            score += 4;
        }
        if (this.driverDocumentStatus(driver) === 'Due Soon') {
            score += 2;
        }
        if (!driver.currentVehicle) {
            score += 1;
        }
        if (!driver.validated && !driver.isIDVerified) {
            score += 1;
        }
        return score;
    }

    private mobilizationsActiveOn(day: Date): number {
        const dayStart = this.startOfDay(day).getTime();
        return this.mobilizations.filter(item => {
            if (!['ACTIVE', 'APPROVED'].includes(item.status || '')) {
                return false;
            }
            const start = this.toDate(item.startDate);
            const end = this.toDate(item.endDate);
            const startTime = start ? this.startOfDay(start).getTime() : dayStart;
            const endTime = end ? this.startOfDay(end).getTime() : dayStart;
            return startTime <= dayStart && endTime >= dayStart;
        }).length;
    }

    private nextDays(count: number): Date[] {
        const today = this.startOfDay(new Date());
        return Array.from({ length: count }, (_, index) => {
            const day = new Date(today);
            day.setDate(today.getDate() + index);
            return day;
        });
    }

    private statusRank(status?: MobilizationStatus): number {
        switch (status) {
            case 'ACTIVE':
                return 0;
            case 'APPROVED':
                return 1;
            case 'SUBMITTED':
                return 2;
            default:
                return 3;
        }
    }

    private percentValue(part: number, total: number): number {
        if (!total) {
            return 0;
        }
        return this.clampPercent((part / total) * 100);
    }

    private percentLabel(value: number): string {
        return `${Math.round(this.clampPercent(value))}%`;
    }

    private clampPercent(value: number): number {
        return Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0));
    }

    private daysUntil(value?: string | Date | number | null): number | null {
        const date = this.toDate(value);
        if (!date) {
            return null;
        }
        const today = this.startOfDay(new Date());
        return Math.ceil((this.startOfDay(date).getTime() - today.getTime()) / 86400000);
    }

    private toDate(value?: string | Date | number | null): Date | null {
        if (value === undefined || value === null || value === '') {
            return null;
        }
        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value;
        }
        if (typeof value === 'number') {
            const date = new Date(value < 10000000000 ? value * 1000 : value);
            return Number.isNaN(date.getTime()) ? null : date;
        }
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    private startOfDay(value: Date): Date {
        const date = new Date(value);
        date.setHours(0, 0, 0, 0);
        return date;
    }
}
